import { forwardRef, type ReactNode } from 'react';

import { cn } from '../cn';
import { Button, type ButtonProps } from './button';
import { Tooltip } from './tooltip';

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon'> {
  /** Required: becomes aria-label and the tooltip text. */
  label: string;
  icon: ReactNode;
  /** Keyboard shortcut shown in the tooltip, e.g. "Ctrl \\". */
  shortcut?: string;
  tooltip?: boolean;
}

/** Square icon-only button. Always labelled for screen readers and has a tooltip. */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, shortcut, tooltip = true, className, size = 'md', variant = 'ghost', ...props },
  ref,
) {
  const sizeClass = size === 'xs' ? 'w-6 px-0' : size === 'sm' ? 'w-7 px-0' : size === 'lg' ? 'w-9 px-0' : 'w-8 px-0';
  const button = (
    <Button ref={ref} aria-label={label} size={size} variant={variant} className={cn(sizeClass, className)} {...props}>
      {icon}
    </Button>
  );
  if (!tooltip) return button;
  return (
    <Tooltip content={label} shortcut={shortcut}>
      {button}
    </Tooltip>
  );
});
